import { useMetadataUser } from "contexts/metadata-user-context";
import { type SetStateAction, useEffect, useRef, useState } from "react";
import {
  type RecurrentResponse as RootRecurrent,
  fetchRecurrent,
} from "services/fetch-subscription";
import {
  type GetInfoDataCardResponse,
  getInfoDataCard,
} from "services/get-data-card";
import { Formatter } from "../../../../utils/formatters";
import type { PaymentFormAdditional } from "../ReservationForm";

type FormIAdditionalPaymentProps = {
  value: PaymentFormAdditional;
  set: (value: PaymentFormAdditional) => void;
};

export function FormAdditionalPayment({
  set,
  value,
}: Readonly<FormIAdditionalPaymentProps>) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value.installments);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [recurrent, setRecurrent] = useState<RootRecurrent | null>(null);
  const [infoPayment, setInfoPayment] =
    useState<GetInfoDataCardResponse | null>(null);
  const { profile } = useMetadataUser();

  const options: { value: number; label: string }[] =
    value.values_installments.reduce<{ value: number; label: string }[]>(
      (acc, installment, index) => {
        acc.push({
          value: index + 1,
          label: Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(installment),
        });
        return acc;
      },
      []
    );

  const handleOutsideClick = (indiceSelection: SetStateAction<number>) => {
    setDropdownOpen(false);
    setSelectedOption(indiceSelection);
  };

  useEffect(() => {
    const fetchRecurrenData = async () => {
      try {
        if (!profile?.subscription?.recurrent_payment_id) {
          throw new Error("Recurrent ID not found");
        }

        const recurrent = await fetchRecurrent(
          profile.subscription.recurrent_payment_id
        );
        setRecurrent(recurrent);
      } catch (error) {
        setRecurrent(null);
      }
    };

    fetchRecurrenData();
  }, [profile?.subscription?.recurrent_payment_id]);

  useEffect(() => {
    const getCardDate = async () => {
      try {
        const paymentId =
          recurrent?.recurrent_payment?.recurrent_transactions[0]?.payment_id;

        if (!paymentId) {
          throw new Error("Recurrent ID not found");
        }
        const response = await getInfoDataCard(paymentId);
        setInfoPayment(response);
      } catch (error) {
        setInfoPayment(null);
      }
    };
    getCardDate();
  }, [recurrent?.recurrent_payment?.recurrent_transactions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as unknown as HTMLElement).contains(
          event.target as Node
        )
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="flex items-center gap-4">
        <label
          htmlFor="Número de Parcelas"
          className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
        >
          Número de Parcelas
        </label>
        <div className="relative" ref={dropdownRef}>
          <div
            className="flex border w-44 items-center justify-between border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3 cursor-pointer"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selectedOption}x{" "}
            {Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(value.values_installments[selectedOption - 1])}
            <svg
              className="w-5 h-5 fill-current text-gray-700 ml-2"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10 12l-4-4h8l-4 4z" />
            </svg>
          </div>
          {dropdownOpen && (
            <ul className="absolute z-10 w-44 bg-white shadow-xl overflow-hidden dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md mt-2">
              {value.values_installments.map((installment, index) => (
                <li
                  key={`installment-${index + 1}`}
                  className="py-2 px-3 flex cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => {
                    handleOutsideClick(options[index].value);
                    set({ ...value, installments: options[index].value });
                  }}
                >
                  {index + 1}x{" "}
                  {Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(installment)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {profile?.wallet.is_card_token ? (
        <div className="flex items-center gap-4 my-4">
          <div className="flex items-center gap-4">
            <input
              type="radio"
              name="credit_card_recurrence"
              id="credit_card_recurrence"
              checked={value.use_credit_card_recurrence}
              onChange={(e) =>
                set({
                  ...value,
                  use_credit_card_recurrence: e.target.checked,
                  use_other_credit_card: false,
                })
              }
              className="rounded-full w-3 h-3 border border-neutral-200 dark:border-neutral-700"
            />
            <label
              htmlFor="credit_card_recurrence"
              className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
            >
              Cartão {infoPayment?.brand} {infoPayment?.card_number}
            </label>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="radio"
              name="usage_other_card"
              id="usage_other_card"
              checked={value.use_other_credit_card}
              onChange={(e) =>
                set({
                  ...value,
                  use_credit_card_recurrence: false,
                  use_other_credit_card: e.target.checked,
                })
              }
              className="rounded-full w-3 h-3 border border-neutral-200 dark:border-neutral-700"
            />
            <label
              htmlFor="usage_other_card"
              className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
            >
              Usar outro cartão
            </label>
          </div>
        </div>
      ) : null}
      {value.use_credit_card_recurrence ? null : (
        <>
          <div>
            <label
              htmlFor="Número do Cartão"
              className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
            >
              Número do Cartão
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={Formatter.prettyNumberCredit(value.card_number)}
              onChange={(e) => {
                if (e.target.value.length <= 16) {
                  set({ ...value, card_number: e.target.value })
                }
              }}
              className="w-80 border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
            />
          </div>
          <div>
            <label
              htmlFor="Nome do Titular"
              className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
            >
              Nome do Titular
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={value.card_holder}
              onChange={(e) => set({ ...value, card_holder: e.target.value })}
              className="w-80 border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-0 md:-space-x-32">
            <div>
              <label
                htmlFor="Data de Validade"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
              >
                Data de Validade
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={Formatter.prettyValidDateCredit(value.card_expiration)}
                onChange={(e) =>
                  set({ ...value, card_expiration: e.target.value })
                }
                className="w-80 border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
              />
            </div>
            <div>
              <label
                htmlFor="CVV/CVC"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
              >
                CVV/CVC
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={Formatter.prettyCVCCredit(value.card_cvv)}
                onChange={(e) => set({ ...value, card_cvv: e.target.value })}
                className="w-80 border border-neutral-200 dark:border-neutral-700 rounded-md py-2 px-3"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="checkbox"
              name="change_recurrent_card"
              id="change_recurrent_card"
              checked={value.change_recurrent_card}
              onChange={(e) =>
                set({
                  ...value,
                  change_recurrent_card: e.target.checked,
                })
              }
              className="rounded-full w-3 h-3 border border-neutral-200 dark:border-neutral-700"
            />
            <label
              htmlFor="change_recurrent_card"
              className="block text-sm font-medium text-neutral-900 dark:text-neutral-200"
            >
              Alterar cartão da recorrência
            </label>
          </div>
        </>
      )}
    </div>
  );
}
